import { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Navigate } from 'react-router-dom'
import { projetoService } from '../services/projeto'
import { Projeto, StatusProjetoRef, PessoaRef } from '../tipos/projeto'
import { useStatusProjeto, useAnalistasSMN } from '../hooks/useProjeto'
import { KanbanColumn } from '../componentes/KanbanColumn'
import { KanbanFilters } from '../componentes/KanbanFilters'
import { ProjetoDetalhesDrawer } from '../componentes/ProjetoDetalhesDrawer'
import { useUsuario } from '../../../contexts/UsuarioContext'

// Mapeamento de colunas do Kanban para status de projeto
// Ordem: Backlog, Escopo, Desenvolvimento, Testes Internos, Testes de Produto, 
// Homologação, Pré Produção, Produção, Cancelado, Pausado, Aguardando Terceiros
const KANBAN_COLUMNS = [
  { id: '1', title: 'Backlog', statusCodigo: 1 },
  { id: '2', title: 'Escopo', statusCodigo: 2 },
  { id: '3', title: 'Desenvolvimento', statusCodigo: 3 },
  { id: '4', title: 'Testes Internos', statusCodigo: 4 },
  { id: '5', title: 'Testes de Produto', statusCodigo: 5 },
  { id: '6', title: 'Homologação', statusCodigo: 6 },
  { id: '7', title: 'Pré Produção', statusCodigo: 10 },
  { id: '8', title: 'Produção', statusCodigo: 11 },
  { id: '9', title: 'Cancelado', statusCodigo: 8 },
  { id: '10', title: 'Pausado', statusCodigo: 7 },
  { id: '11', title: 'Aguardando Terceiros', statusCodigo: 9 },
]

interface KanbanFilters {
  analistaSMNId?: number | null
  statusCodigo?: number | null
  search?: string | null
}

function Kanban() {
  const { usuario } = useUsuario()
  const [filters, setFilters] = useState<KanbanFilters>({})
  const [selectedProjeto, setSelectedProjeto] = useState<Projeto | null>(null)
  const [visibleColumns, setVisibleColumns] = useState<Set<number>>(
    new Set(KANBAN_COLUMNS.map(col => col.statusCodigo))
  )
  
  const { data: statusProjetos } = useStatusProjeto()
  const { data: analistasSMN } = useAnalistasSMN()

  // Verificar se o usuário é administrador (papel '1' = Administrador)
  const isAdministrador = usuario?.papeis?.includes('1') ?? false

  // Redirecionar se não for administrador
  if (!isAdministrador) {
    return <Navigate to="/dashboard" replace />
  }

  // Filtrar colunas visíveis
  const colunasVisiveis = KANBAN_COLUMNS.filter(col => visibleColumns.has(col.statusCodigo))

  // Buscar todos os projetos (filtros aplicados no backend)
  const { data: projetosResponse, isLoading } = useQuery(
    ['projetos-kanban', filters],
    () => projetoService.buscarProjetos({
      statusCodigo: filters.statusCodigo ? Number(filters.statusCodigo) : undefined,
      analistaSMNId: filters.analistaSMNId ? Number(filters.analistaSMNId) : undefined,
      search: filters.search || undefined,
    }),
    {
      keepPreviousData: true,
    }
  )

  const projetos = projetosResponse?.data || []

  // Agrupar projetos por status
  const projetosPorColuna = useMemo(() => {
    const agrupados: Record<string, Projeto[]> = {}
    
    // Inicializar apenas colunas visíveis
    colunasVisiveis.forEach(col => {
      agrupados[col.id] = []
    })

    // Agrupar projetos por status
    projetos.forEach(projeto => {
      const statusCodigo = Number(projeto.statusCodigo)
      const coluna = colunasVisiveis.find(col => col.statusCodigo === statusCodigo)
      
      if (coluna) {
        if (!agrupados[coluna.id]) {
          agrupados[coluna.id] = []
        }
        agrupados[coluna.id].push(projeto)
      }
    })

    return agrupados
  }, [projetos, colunasVisiveis])

  // Contar filtros ativos
  const activeFilterCount = useMemo(() => {
    return [filters.analistaSMNId, filters.statusCodigo, filters.search].filter(Boolean).length
  }, [filters])


  return (
    <div className="h-full w-full flex flex-col bg-gray-50" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Header fixo estilo Trello */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Kanban de Projetos</h1>
            <p className="text-sm text-gray-500 mt-1">Visualize e gerencie seus projetos</p>
          </div>
        </div>

        {/* Filtros organizados em layout harmonioso */}
        <div className="border-t border-gray-100 pt-4">
          <KanbanFilters
          filters={filters}
          onFiltersChange={setFilters}
          statusProjetos={(statusProjetos || []) as StatusProjetoRef[]}
          analistasSMN={(analistasSMN || []) as PessoaRef[]}
          activeFilterCount={activeFilterCount}
          visibleColumns={visibleColumns}
          allColumns={KANBAN_COLUMNS}
          onToggleColumn={(statusCodigo) => {
            const newVisible = new Set(visibleColumns)
            if (newVisible.has(statusCodigo)) {
              newVisible.delete(statusCodigo)
            } else {
              newVisible.add(statusCodigo)
            }
            setVisibleColumns(newVisible)
          }}
          onSelectAllColumns={(selectAll) => {
            if (selectAll) {
              // Selecionar todas as colunas
              const allStatusCodes = new Set(KANBAN_COLUMNS.map(col => col.statusCodigo))
              setVisibleColumns(allStatusCodes)
            } else {
              // Desmarcar todas as colunas
              setVisibleColumns(new Set())
            }
          }}
          />
        </div>
      </div>

      {/* Kanban Board com scroll horizontal suave (estático - sem drag and drop) */}
      <div 
        className="flex-1 bg-gray-50 kanban-scroll-container" 
        style={{ 
          minHeight: 0,
          width: '100%',
          overflowX: 'scroll',
          overflowY: 'hidden'
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
          </div>
        ) : (
            <div 
              className="inline-flex h-full gap-4 p-4" 
              style={{ 
                // Largura total: (colunas × 288px) + (gaps × (colunas - 1)) + (padding 32px)
                // 304px = 288px (coluna) + 16px (gap médio por coluna)
                minWidth: `${colunasVisiveis.length * 304 + 32}px`,
                width: `${colunasVisiveis.length * 304 + 32}px`,
                flexShrink: 0
              }}
            >
              {colunasVisiveis.map(coluna => (
                <KanbanColumn
                  key={coluna.id}
                  id={coluna.id}
                  title={coluna.title}
                  statusCodigo={coluna.statusCodigo}
                  projetos={projetosPorColuna[coluna.id] || []}
                  onCardClick={setSelectedProjeto}
                  isOver={false}
                  isReadOnly={true}
                />
              ))}
            </div>
          )}
      </div>

      {/* Drawer lateral para detalhes (estilo Trello) */}
      {selectedProjeto && (
        <ProjetoDetalhesDrawer
          projeto={selectedProjeto}
          isOpen={!!selectedProjeto}
          onClose={() => setSelectedProjeto(null)}
        />
      )}
    </div>
  )
}

export default Kanban
